import {
  IAuthClient,
  IAnalyticsResponse,
  IExperimentList,
  IFollowersListResponse,
  IGallery,
  IGifInfo,
  IMeGifsResponse,
  IGifList,
  IUser,
  IUserSearchResults,
  IUserSettings,
  IAdSlotsResponse,
  IAdManageSlotsResponse,
  IGlobalNotifications,
  IAliasLink,
  IStreamateCamera,
  IStreamateCameraRequest,
  ICreatorTagsResponse,
  VerifyResponse,
  ITagFeedbackPayload,
  ITagLandingFeed,
  IGeolocation,
  IAdsCreators,
  IPaginatedNicheList,
  OnlyFansSource,
  IFeedModulesResponse,
  IAgeGateSettingsResponse,
} from "./types";

import AuthHttpService from "./AuthHttpService";

interface IGifReportParams {
  gifId: string;
  option: number;
  message: string;
  canContact: boolean;
  email: string;
}

interface ILikeGifParams {
  gifId: string;
  context: string | null;
  source: string;
  sourceId: string | null;
  position: number;
}

interface IUnlikeGifParams {
  gifId: string;
  context: string | null;
}

export interface IUserGifsParams {
  order: string | null;
  isPublic: string | null;
  type: string;
  count: string | null;
  page: number | null;
  tags?: string[];
  isTrigger?: boolean;
}

export interface IOperation {
  op?: string;
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

interface IPatchGifParams {
  hide_trending?: boolean;
  hide_home?: boolean;
  sexuality?: string;
  published?: boolean;
  description?: string;
  niches?: string[];
  tags?: string[] | null;
}

export type TrendingFeedVariation = "curated" | "popular" | "verified" | "established";

interface IOAuthCodeParams {
  client_id: string;
  scope: string;
  redirect_uri: string;
  response_type: string;
  state: string;
}

interface IOAuthCodeResult {
  url: string;
}

interface IVerifyAgeResult {
  sessionUrl: string;
}

interface IAgeVerification {
  status: string;
}

interface IFollowUserParams {
  username: string;
  source: string;
  position: number;
  sourceId: string | null;
}

export default class RedGifsService extends AuthHttpService {
  public constructor(readonly url?: string) {
    super(url);
  }

  async fetchGifById(gifId: string): Promise<IGifInfo> {
    return await this.get(`v2/gifs/${gifId}?views=yes&users=yes&niches=yes`);
  }

  async getExperiments(): Promise<IExperimentList> {
    const query = new URLSearchParams();
    query.append("source", window.location.href);

    return await this.get(`v2/experiments/all?${query.toString()}`);
  }

  async getForYouFeed(props: {
    page: number;
    count: number;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    return await this.get(`v2/feeds/for-you?${query.toString()}`, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async getTrendingFeed(props: {
    page: number;
    count: number;
    variation: TrendingFeedVariation;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    return await this.get(`v2/feeds/trending/${props.variation}?${query.toString()}`, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async getLikedFeed(props: {
    page: number;
    count: number;
    type?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.page) {
      query.append("page", props.page.toString());
    }

    if (props.count) {
      query.append("count", props.count.toString());
    }

    if (props.type) {
      query.append("type", props.type);
    }

    return await this.get(`v2/feeds/liked?${query.toString()}`);
  }

  async hideGif(props: {
    id: string;
    home: boolean;
    trending: boolean;
  }): Promise<void> {
    await this.patch(`v2/gifs/${props.id}`, {
      hide_home: props.home,
      hide_trending: props.trending,
    });
  }

  async likeGifById({ gifId, context, source, sourceId, position }: ILikeGifParams) {
    return await this.put(`v2/gifs/${gifId}/like`, {
      context,
      source,
      source_id: sourceId,
      position,
    });
  }

  async unlikeGifById(params: IUnlikeGifParams) {
    return await this.delete(`v2/gifs/${params.gifId}/like`, {
      context: params.context,
    });
  }

  async searchUserGifs(params: {
    username: string;
    order: string;
    count: number;
    type?: string;
    page?: number;
    tags?: string;
    verified?: boolean;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (params.order) {
      query.append("order", params.order);
    }

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.type) {
      query.append("type", params.type);
    }

    if (params.page) {
      query.append("page", params.page.toString());
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const queryString = query.toString();
    return await this.get(`v2/users/${params.username}/search?${queryString}`);
  }

  async searchMyGifs(params: {
    order: string;
    count: number;
    type?: string;
    page?: number;
    tags?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (params.order) {
      query.append("order", params.order);
    }

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.type) {
      query.append("type", params.type);
    }

    if (params.page) {
      query.append("page", params.page.toString());
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const queryString = query.toString();
    return await this.get(`v2/me/content?${queryString}`);
  }

  async searchSuggestions(payload: string){
    return await this.get(`v1/tags/suggest?query=${payload}`);
  }

  async fetchCreatorById(creatorId: string): Promise<IUser> {
    return await this.get(`v1/users/${creatorId}`);
  }

  async fetchCreatorTagsById(creatorId: string): Promise<ICreatorTagsResponse> {
    return await this.get(`v2/creators/${creatorId}/tags`);
  }

  async reportGif(params: IGifReportParams) {
    return await this.post(`v1/gifs/${params.gifId}/report-content`, {
      option: params.option,
      message: params.message,
      canContact: params.canContact,
      email: params.email
    });
  }

  async getAuthClient(id: string): Promise<IAuthClient> {
    return await this.get(`v2/auth/clients/${encodeURIComponent(id)}`);
  }

  async getMe(): Promise<IUser> {
    return await this.get("v1/me", {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async updateProfileInfo(operations: IOperation[]): Promise<void> {
    return await this.patch("v1/me", { operations });
  }

  async deleteMyAccount() {
    return await this.delete("v1/me");
  }

  async followUser({ username, source, sourceId, position }: IFollowUserParams): Promise<void> {
    await this.put(`v1/me/follows/${username}`, {
      source,
      source_id: sourceId,
      position,
    });
  }

  async unfollowUser(username: string): Promise<void> {
    await this.delete(`v1/me/follows/${username}`);
  }

  async fetchUserFollowers(props: {
    count?: number;
  }): Promise<IFollowersListResponse> {
    const params = new URLSearchParams();

    if (props.count) {
      params.append("count", props.count.toString());
    }

    return await this.get(`v1/me/followers/populated?${params.toString()}`);
  }

  async getFollowing(props: {
    count: number;
    page: number;
  }): Promise<IUserSearchResults> {
    const params = new URLSearchParams();
    params.append("count", props.count.toString());
    params.append("page", props.page.toString());

    return await this.get(`v2/me/following?${params.toString()}`);
  }

  async getFollowers(props: {
    count: number;
    page: number;
  }): Promise<IUserSearchResults> {
    const params = new URLSearchParams();
    params.append("count", props.count.toString());
    params.append("page", props.page.toString());

    return await this.get(`v2/me/followers?${params.toString()}`);
  }

  async getFollowingIds(): Promise<string[]> {
    return await this.get("v1/me/follows");
  }

  async deleteGif (gifId: string): Promise<void> {
    return await this.delete(`v1/me/gifs/${gifId}`);
  }

  async patchGif(id: string, params: IPatchGifParams) {
    const response = await this.patch(`v2/gifs/${id}`, params);
    return response;
  }

  fetchUserGifs ({ type, order, isPublic, count, page, tags }: IUserGifsParams): Promise<IMeGifsResponse> {
    const URLParams = new URLSearchParams({
      type,
    });

    if (order !== null) {
      URLParams.append("order", order);
    }

    if (isPublic !== null) {
      URLParams.append("public", isPublic === "1" ? "yes" : "no");
    }

    if (count !== null) {
      URLParams.append("count", count);
    }

    if (page !== null) {
      URLParams.append("page", page.toString());
    }

    if (tags && tags.length > 0) {
      URLParams.append("tags", tags.join(","));
    }

    return this.get(`v2/me/content?${URLParams.toString()}`);
  }

  async getLikedGifs(): Promise<string[]> {
    return await this.get("v2/likes");
  }

  async getGallery (galleryId: string): Promise<IGallery> {
    return await this.get(`v2/gallery/${galleryId}`);
  }

  async getRelatedGifs(params: {
    id: string;
    page: number;
    count: number;
  }): Promise<IGifList> {
    const url = `v2/recommend/tags/${params.id}?page=${params.page}&count=${params.count}`;

    return await this.get(url, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async getAIGifRecommendations(params: {
    id: string;
    count: number;
    cursor: string | undefined;
    filter?: string;
    scenario?: string;
    tags?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();
    query.append("count", params.count.toString());

    if (params.cursor) {
      query.append("recommendation_id", params.cursor);
    }

    if (params.filter) {
      query.append("filter", params.filter);
    }

    if (params.scenario) {
      query.append("scenario", params.scenario);
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const url = `v2/recommend/ai/${params.id}?${query.toString()}`;

    return await this.get(url, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async getAITrendingRecommendations(props: {
    recommendationId: string | null;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (props.recommendationId) {
      query.append("recommendation_id", props.recommendationId);
    }

    return await this.get(`v2/recommend/ai/trending?${query.toString()}`, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async updateAIUser(): Promise<void> {
    return await this.patch("v2/recommend/ai/user", {}, {
      "X-Session-Id": await this.getSessionId(),
    });
  }

  async getUser(params: {
    userName: string;
  }): Promise<IUser> {
    return await this.get(`v1/users/${params.userName}`);
  }

  async getOAuthCode(params: IOAuthCodeParams): Promise<IOAuthCodeResult> {
    return await this.post("/v2/oauth/code", params);
  }

  async getVerifyUserStatus(): Promise<VerifyResponse> {
    return await this.get("v2/me/verify-profile");
  }

  async getAnalytics(): Promise<IAnalyticsResponse> {
    return await this.get("v2/analytics");
  }

  updateUserSettings(params: IUserSettings): Promise<void> {
    return this.patch("v2/me/settings", params);
  }

  async getGifNiches(gifId: string): Promise<IPaginatedNicheList> {
    return await this.get(`v2/gifs/${gifId}/niches`);
  }

  async checkGifSound(gifId: string) {
    return await this.post(`v2/gifs/${gifId}/check-sound`);
  }

  async getAdSlots(): Promise<IAdSlotsResponse> {
    return await this.get("v2/adv/slots");
  }

  async getAdManageSlots(): Promise<IAdManageSlotsResponse> {
    return await this.get("v2/adv/manage/slots");
  }

  async updateAdManageSlots(names: string[]) {
    return await this.post("v2/adv/manage/slots", {
      slots: names
    });
  }

  /**
   * Request a single Streamate camera.
   */
  async getStreamateCamera(req: IStreamateCameraRequest): Promise<IStreamateCamera> {
    const query = new URLSearchParams();

    if (req.exclude.length > 0) {
      query.append("exclude", req.exclude.join(","));
    }

    const res = await this.get<Promise<{camera:IStreamateCamera}>>(`v2/cameron/streamate/camera?${query.toString()}`);

    return res.camera;
  }

  async getPinGifs(userName: string): Promise<IGifList> {
    return await this.get(`v2/pins/${userName}`);
  }

  async addPinGif(gif: string): Promise<void> {
    return await this.post("v2/pins", { gif });
  }

  async unPinGif(gif: string): Promise<void> {
    return await this.delete(`v2/pins/${gif}`);
  }

  async reorderPinGifs(gifs: string[]): Promise<void> {
    return await this.put("v2/pins", { ids: gifs });
  }

  async getGlobalNotifications(): Promise<IGlobalNotifications> {
    return await this.get("v2/notify/broadcast");
  }

  async getLastSeenNotification(): Promise<{ id: string }> {
    return await this.get("v2/notify/broadcast/last-seen");
  }

  async deleteGlobalNotification(id: string): Promise<void> {
    return await this.delete(`v2/notify/broadcast/${id}`);
  }

  async addAliasLink(gifId: string): Promise<IAliasLink> {
    return await this.post("v2/alias", { gif: gifId });
  }

  async getAliasLinks(gifId: string): Promise<IAliasLink[]> {
    return await this.get(`v2/alias/gif/${gifId}`);
  }

  async getAliasLink(aliasId: string) {
    return await this.get(`v2/alias/${aliasId}`);
  }

  async removeAliasLink(aliasId: string) {
    return await this.delete(`v2/alias/${aliasId}`);
  }

  async getGifTagsFeedback(gifId: string): Promise<{ tags: string[] }> {
    return await this.get(`v2/gifs/${gifId}/tag-feedback`);
  }

  async updateGifTagFeedback(gifId: string, payload: ITagFeedbackPayload) {
    return await this.put(`v2/gifs/${gifId}/tag-feedback`, payload);
  }

  async getRedditRecs(params: {
    id: string;
    count: number;
    page: number;
    cursor?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();
    query.append("count", params.count.toString());
    query.append("page", params.page.toString());

    if (params.cursor) {
      query.append("recommendation_id", params.cursor);
    }

    return await this.get(`/v2/recommend/gifs/${params.id}/reddit?${query.toString()}`);
  }

  async getQuickRecs(params: { id: string }): Promise<IGifList> {
    return await this.get(`/v2/recommend/gifs/${params.id}/quick`);
  }

  async getAdsCreators(params: { source: OnlyFansSource; count: number }): Promise<IAdsCreators> {
    return await this.get(`/v2/adv/creators-traffic?count=${params.count.toString()}&source=${params.source}`);
  }

  async getTagLandingFeed(params: { tag: string }): Promise<ITagLandingFeed> {
    return await this.get(`/v2/tags/feed/${params.tag}`);
  }

  async getGeolocation(): Promise<IGeolocation> {
    return await this.get("/v2/geolocation");
  }

  async getSFWFeed(params: { page: number; count: number }): Promise<IGifList> {
    const query = new URLSearchParams();
    query.append("page", params.page.toString());
    query.append("count", params.count.toString());
    return await this.get(`v2/feeds/sfw?${query.toString()}`);
  }

  async verifyAge(): Promise<IVerifyAgeResult> {
    return await this.post("/v2/me/verify-age");
  }

  async getAgeVerificationStatus(sessionId: string): Promise<IAgeVerification> {
    return await this.get(`/v2/me/verify-age?session_id=${sessionId}`);
  }

  async getFeedModules(): Promise<IFeedModulesResponse> {
    return await this.get("/v2/feeds/modules");
  }

  async getAgeGateSettings(): Promise<IAgeGateSettingsResponse> {
    return await this.get("/v2/me/settings/age-gate");
  }
}
